import { Autocomplete, FormControl, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import { ChangeEvent, useCallback, useEffect, useState, VFC } from "react";
import ButtonArea from "../common/ButtonArea";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { BackButton } from "../common/BackButton";
import { useNavigate } from "react-router-dom";
import { TerminalImportConfirmForm, TerminalImportConfirmFormSchema, TerminalImportConfirmResponse } from "./api/TerminalForm";
import { SubmitHandler, useForm, Controller } from "react-hook-form"; // Add Controller import
import { yupResolver } from "@hookform/resolvers/yup";
import { TerminalService } from "./api/TerminalService";
import { AxiosResponse } from "axios";
import Constants from "../../utils/Constants";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { useDropzone } from "react-dropzone";
import { PullDownItem } from "../common/form/FormPullDown";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameRequest, TenantNameResponse } from "../tenant/api/TenantForm";
import { Utility } from "../../utils/Utility";
import { Loading } from "../common/Loading";

/**
 * 端末インポート画面
 */
const TerminalImport: VFC = () => {
  const navigate = useNavigate();
  const PAGE_NAME = "端末インポート";

  /***************************************************************
   * State管理
   **************************************************************/
  // ファイル名
  const [selectFileName, setSelectFileName] = useState("");

  // インポート確認結果情報
  const [importCheckResponse, setImportCheckResponse] =
    useState<TerminalImportConfirmResponse | null>(null);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  // ローディング
  const [isLoading, setIsLoading] = useState(false);

  // テナント選択肢
  const [tenantOption, setTenantOption] = useState<PullDownItem[]>([]);
  const [tenantName, setTenantName] = useState("");

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    if (null !== importCheckResponse) {
      navigate("/terminal_import_confirmation", {
        state: { importCheckResponse: importCheckResponse },
      });
    }
    document.querySelectorAll("input[type=file]").forEach(input => {
      input.addEventListener("cancel", onFileDialogCancel);
    });
  }, [importCheckResponse]);

  useEffect(() => {
    getTenantNameList();
  }, []);

  // テナント名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        setTenantOption(Utility.getTenantNameOption(tenantNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  /***************************************************************
   * インポート処理
   **************************************************************/
  // ファイル選択イベント
  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setValue("csvFile", file);
      setSelectFileName(file.name);
    } else {
      onFileDialogCancel();
    }
  };
  const onFileDialogCancel = () => {
    setValue("csvFile", null);
    setSelectFileName("");
  };

  // ドラッグ＆ドロップでファイルを選択
  const onDrop = useCallback((acceptedFiles) => {
    const files: FileList | null = acceptedFiles;
    if (files) {
      const file = files[0];
      setValue("csvFile", file);
      setSelectFileName(file.name);
    } else {
      setValue("csvFile", null);
      setSelectFileName("");
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, onFileDialogCancel });


  // インポートボタン押下(入力チェック)
  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<TerminalImportConfirmForm>({
    resolver: yupResolver(TerminalImportConfirmFormSchema),
    reValidateMode: "onSubmit",
  });
  // 端末インポート確認API
  const handleImportBtn: SubmitHandler<TerminalImportConfirmForm> = (hookForm) => {
    const service = new TerminalService();

    // インポートデータ
    let formData = new FormData();
    formData.append("uploadFile", getValues("csvFile"));
    formData.append("tenantId", getValues("tenant"));

    setIsLoading(true);

    service
      .importConfirm(formData)
      .then((response: AxiosResponse) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          response.data.result.tenantName = tenantName;
          setImportCheckResponse(response.data.result);
        } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
          // INVALID
          setImportCheckResponse({ ...response.data.result, isError: true });
        } else {
          // NG
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fileInputForm = () => {
    const handleTenantAutocompleteChange = (e: React.SyntheticEvent, value: PullDownItem | null) => {
      if (value?.key) {
        setTenantName(value.value);
        setValue("tenant", value.key);
      }
    }
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8} sx={{ pb: 5, pr: 2 }} {...getRootProps()}>
          <TextField
            required
            label="ファイル名"
            name="fileName"
            fullWidth
            size="small"
            placeholder="ここにドラッグ＆ドロップするかクリックして選択してください"
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            value={selectFileName}
            error={"csvFile" in errors}
            helperText={errors.csvFile?.message}
          />
          <input {...getInputProps()} />
        </Grid>
        <Grid item xs={4} sx={{ pb: 5 }}>
          <Button variant="contained" component="label">
            ファイルを選択
            <input type="file" hidden onChange={onFileChange} />
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ pb: 5, pr: 2 }}>
          <Autocomplete
            size="small"
            fullWidth
            // multiple
            options={tenantOption ?? []}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, v) =>
              option.key == v.key
            }
            // defaultValue={cache.value.tenantValue}
            onChange={handleTenantAutocompleteChange}
            renderInput={(params) => (
            <TextField
              required
              {...params} name="tenantIds"
              label="テナント"
              InputLabelProps={{
                shrink: true
              }}
              error={"tenant" in errors}
              helperText={errors.tenant?.message}
            />
          )}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"column"}
        style={{ height: "calc(100vh - 64px)", width: "calc(100vw - 200px)" }}
      >
        <ButtonArea pageName={PAGE_NAME} isBackBtn={true}>
          <Box mr={2}>
            <Button variant="contained" color="primary" onClick={handleSubmit(handleImportBtn)}>
              インポート
            </Button>
          </Box>
        </ButtonArea>
        <Box sx={{ p: 4 }} flex={"1"} style={{ overflowY: "scroll" }}>{fileInputForm()}</Box>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default TerminalImport;
